/**
 * Time Attack Game Mode
 * 
 * Race against the clock! Clear as many cells as possible within the time limit.
 * Every match extends your time. Score multiplier increases with consecutive matches.
 * 
 * Features:
 * - 2-minute (120 seconds) countdown timer
 * - Time bonus for each match (+5 seconds)
 * - Fixed difficulty level for consistent gameplay
 * - Game ends when time runs out
 */

import { BaseGameMode, GameState, ModeConfig } from '../core/BaseGameMode'
import { Grid } from '../Grid'
import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { FallingPair, PairGenerator, PairConfiguration } from '../FallingPair'
import { GameBalanceManager } from '../GameBalanceManager'
import { TimeAttackMatchingEngine } from './TimeAttackMatchingEngine'

export class TimeAttackGameMode extends BaseGameMode {
    // Time Attack mode specific state
    private nextPairs: PairConfiguration[]
    private callbacks: Map<string, Function[]>

    // Timer state
    private timeRemaining: number // seconds
    private readonly initialTime: number = 120 // 2 minutes in seconds
    private readonly timeExtensionPerMatch: number = 5 // seconds
    private readonly minimumTimeExtension: number = 2 // minimum seconds added
    private lastTimerUpdate: number = 0

    constructor(
        grid: Grid,
        matchingEngine: BaseMatchingEngine,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ) {
        super(grid, matchingEngine, pairGenerator, balanceManager, config)

        this.nextPairs = []
        this.callbacks = new Map()
        this.timeRemaining = this.initialTime
    }

    /**
     * Initialize Time Attack mode - generate first set of pairs and start timer
     */
    public initialize(): void {
        this.timeRemaining = this.initialTime
        this.lastTimerUpdate = Date.now()
        this.gameState = GameState.PLAYING
        this.generateNextPairs()
        this.spawnNextPair()
        this.emit('gameStarted', this.stats)
        this.emit('timeUpdated', this.timeRemaining)
    }

    /**
     * Update game state based on delta time
     */
    public update(deltaTime: number): void {
        if (
            this.gameState === GameState.PAUSED ||
            this.gameState === GameState.WAITING_TO_START ||
            this.gameState === GameState.GAME_OVER ||
            this.gameState === GameState.VICTORY
        ) {
            return
        }

        this.updateTimeStats()
        this.updateTimer(deltaTime)

        switch (this.gameState) {
            case GameState.PLAYING:
                this.updatePlaying(deltaTime)
                break
            case GameState.FALLING:
                this.updateFalling(deltaTime)
                break
            case GameState.MATCHING:
                this.updateMatching()
                break
        }
    }

    /**
     * Handle player input
     */
    public handleInput(action: string): boolean {
        // Handle pause/unpause regardless of game state
        if (action === 'pause') {
            this.pauseGame()
            return true
        }

        if (!this.currentPair || this.gameState !== GameState.PLAYING) {
            return false
        }

        switch (action) {
            case 'moveLeft':
                return this.currentPair.moveHorizontal(-1)
            case 'moveRight':
                return this.currentPair.moveHorizontal(1)
            case 'rotateClockwise':
                return this.currentPair.rotate(1)
            case 'rotateCounterClockwise':
                return this.currentPair.rotate(-1)
            case 'softDrop':
                return this.currentPair.moveDown()
            case 'hardDrop':
                this.currentPair.hardDrop()
                return true
            case 'purify':
                return this.tryActivatePurify()
            default:
                return false
        }
    }

    /**
     * Pause the game and emit event
     */
    public pauseGame(): void {
        super.pauseGame()
        if (this.gameState === GameState.PAUSED) {
            this.emit('gamePaused')
        }
    }

    /**
     * Resume the game and emit event
     */
    public resumeGame(): void {
        super.resumeGame()
        if (this.gameState === GameState.PLAYING) {
            this.lastTimerUpdate = Date.now()
            this.emit('gameResumed')
        }
    }

    /**
     * Get preview of next pairs
     */
    public getNextPairs(): PairConfiguration[] {
        return [...this.nextPairs]
    }

    /**
     * Get time remaining in seconds
     */
    public getTimeRemaining(): number {
        return this.timeRemaining
    }

    /**
     * Get score multiplier from matching engine
     */
    public getScoreMultiplier(): number {
        const engine = this.matchingEngine as TimeAttackMatchingEngine
        return engine.getScoreMultiplier()
    }

    /**
     * Register event callbacks
     */
    public on(event: string, callback: Function): void {
        if (!this.callbacks.has(event)) {
            this.callbacks.set(event, [])
        }
        this.callbacks.get(event)!.push(callback)
    }

    /**
     * Remove event callbacks
     */
    public off(event: string, callback?: Function): void {
        if (!this.callbacks.has(event)) return

        if (callback) {
            const callbacks = this.callbacks.get(event)!
            const index = callbacks.indexOf(callback)
            if (index > -1) {
                callbacks.splice(index, 1)
            }
        } else {
            this.callbacks.set(event, [])
        }
    }

    /**
     * Backward compatible helper for tests expecting getActivePiece()
     */
    public getActivePiece(): { rotation: number; exists: boolean; x: number; y: number } | null {
        if (!this.currentPair) return null
        const state = this.currentPair.getState()
        return { rotation: state.rotation, exists: true, x: state.x, y: state.y }
    }

    /**
     * Get the current falling pair (for rendering)
     */
    public getCurrentPair(): FallingPair | null {
        return this.currentPair
    }

    // ========================================
    // Protected Methods (Mode-Specific Logic)
    // ========================================

    /**
     * Check if game should end - Time Attack mode ends when time runs out
     */
    protected checkEndConditions(): void {
        if (this.timeRemaining <= 0) {
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        } else if (this.grid.isOverflowing()) {
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        }
        // No victory condition - Time Attack mode continues until time runs out
    }

    /**
     * Calculate score for cleared cells with chain multiplier
     */
    protected calculateScore(cellsCleared: number, chainLevel: number): number {
        // Score is calculated by matching engine with score multiplier
        return cellsCleared * 10 * (chainLevel || 1)
    }

    // ========================================
    // Private Methods (Update Loop)
    // ========================================

    /**
     * Update the countdown timer
     */
    private updateTimer(deltaTime: number): void {
        const now = Date.now()
        const elapsed = (now - this.lastTimerUpdate) / 1000
        this.lastTimerUpdate = now

        this.timeRemaining = Math.max(0, this.timeRemaining - elapsed)

        this.emit('timeUpdated', this.timeRemaining)

        if (this.timeRemaining <= 0) {
            this.checkEndConditions()
        }
    }

    /**
     * Add time bonus for successful match
     */
    private addTimeBonus(chainLevel: number): void {
        // Base time extension
        let timeBonus = this.timeExtensionPerMatch

        // Bonus time for longer chains
        if (chainLevel > 1) {
            timeBonus += (chainLevel - 1) * this.minimumTimeExtension
        }

        this.timeRemaining += timeBonus
        this.emit('timeBonusAwarded', timeBonus)
        this.emit('timeUpdated', this.timeRemaining)
    }

    /**
     * Update the playing state
     */
    private updatePlaying(deltaTime: number): void {
        if (!this.currentPair) {
            this.spawnNextPair()
            return
        }

        const stillFalling = this.currentPair.update(deltaTime)

        if (!stillFalling) {
            // Pair has locked
            this.stats.pairsDropped++
            this.currentPair = null
            this.gameState = GameState.MATCHING
            this.emit('pairLocked')
        }
    }

    /**
     * Update the falling state (cells falling due to gravity)
     */
    private updateFalling(deltaTime: number): void {
        // Optional chain pacing pause (shared approach with Endless)
        if ((this as any)._chainPauseMs && (this as any)._chainPauseMs > 0) {
            (this as any)._chainPauseMs -= deltaTime
            if ((this as any)._chainPauseMs > 0) {
                return
            }
        }
        const cellsMoved = this.grid.applyGravity()

        if (!cellsMoved) {
            // All cells have settled, check for new matches
            this.gameState = GameState.MATCHING
        }
    }

    /**
     * Update the matching state
     */
    private updateMatching(): void {
        const matchResult = this.matchingEngine.processMatches()

        if (matchResult.totalCellsCleared > 0) {
            // Process match results
            this.processMatchResult(matchResult)

            // Add time bonus for successful match
            this.addTimeBonus(matchResult.chainLevel)

            // Check if more cells need to fall
            this.gameState = GameState.FALLING
        } else {
            // No matches, check game state
            this.checkEndConditions()

            if (this.gameState !== GameState.GAME_OVER && this.gameState !== GameState.VICTORY) {
                this.gameState = GameState.PLAYING
                this.spawnNextPair()
            }
        }
    }

    /**
     * Update purify mode
     */
    private updatePurifyMode(): void {
        // Purify mode is legacy - kept for compatibility
        // In Time Attack mode, no special purify logic
        this.gameState = GameState.FALLING
        this.emit('purifyCompleted', { totalCellsCleared: 0 })
    }

    /**
     * Attempt to activate purify mode (disabled in Time Attack)
     */
    private tryActivatePurify(): boolean {
        // No purify mode in simplified Time Attack
        return false
    }

    /**
     * Spawn the next falling pair
     */
    private spawnNextPair(): void {
        if (this.nextPairs.length === 0) {
            this.generateNextPairs()
        }

        const nextConfig = this.nextPairs.shift()!
        this.currentPair = new FallingPair(nextConfig, this.grid)

        // In Time Attack mode, use fixed difficulty level 3 for consistent gameplay
        const fixedDifficultyLevel = 3
        const fallSpeed = this.balanceManager.calculateFallSpeed(fixedDifficultyLevel)
        this.currentPair.setFallSpeed(fallSpeed)

        // Check if the new pair can be placed
        if (this.currentPair.isLocked()) {
            // Grid is full, game over
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        } else {
            this.emit('pairSpawned', nextConfig)
        }
    }

    /**
     * Generate the next set of pairs with balanced cell distribution
     */
    private generateNextPairs(): void {
        // In Time Attack mode, use fixed difficulty level 3 for consistent gameplay
        const fixedDifficultyLevel = 3

        // In Time Attack mode, all cell types are treated equally
        this.pairGenerator.adjustWeights(0, fixedDifficultyLevel)

        // Generate preview pairs
        this.nextPairs = this.pairGenerator.previewNext(5)
    }

    /**
     * Process the result of matches
     */
    private processMatchResult(result: MatchResult): void {
        this.stats.score += result.scoreGained
        this.stats.totalCellsCleared += result.totalCellsCleared
        this.stats.longestChain = Math.max(this.stats.longestChain, result.chainLevel)

        // Update balance manager with match data
        this.balanceManager.recordMatchResult({
            score: result.scoreGained,
            chainLevel: result.chainLevel,
            cellsCleared: result.totalCellsCleared,
            engraftmentLevel: 3 // Fixed difficulty level for Time Attack
        })

        // No engraftment system in simplified Time Attack mode
        this.emit('matchProcessed', result)

        // Chain pacing delay for dramatic effect
        if (result.chainLevel > 1) {
            const BASE_CHAIN_PAUSE = 300
            const EXTRA_PER_LEVEL = 100
            const pause = BASE_CHAIN_PAUSE + (result.chainLevel - 2) * EXTRA_PER_LEVEL
                ; (this as any)._chainPauseMs = pause
        } else {
            ; (this as any)._chainPauseMs = 0
        }
    }

    /**
     * Emit events to registered callbacks
     */
    protected emit(event: string, data?: any): void {
        const callbacks = this.callbacks.get(event)
        if (callbacks) {
            callbacks.forEach(callback => callback(data))
        }
    }
}
